import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
  onSwitchToPasswordReset: () => void;
  onRequire2FA: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onSwitchToPasswordReset,
  onRequire2FA,
}) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((state) => state.setAuth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login({
        username,
        email: username, // API accepts both
        password,
      });

      setAuth(
        { id: 1, username, email: username },
        response.access_token,
        response.refresh_token
      );

      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Handle validation errors
        setError(detail.map((e: any) => e.msg).join(', '));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(t('auth:login.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('auth:login.title')} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('auth:login.username')}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Value"
          required
        />

        <Input
          label={t('auth:login.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Value"
          showPasswordToggle
          required
        />

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        <Button type="submit" fullWidth disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={onSwitchToPasswordReset}
            className="text-sm text-blue-600 hover:underline"
          >
            Reset Password
          </button>
        </div>
      </form>
    </Modal>
  );
};
